import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import { useWebSocket, type WsMessage, type WsStatus } from '../../hooks/use-websocket';

type MessageHandler = (msg: WsMessage) => void;

interface WSContextValue {
  status: WsStatus;
  send: (msg: WsMessage) => void;
  subscribe: (type: string, handler: MessageHandler) => () => void;
}

const WSContext = createContext<WSContextValue>({
  status: 'disconnected',
  send: () => {},
  subscribe: () => () => {},
});

export interface WSProviderProps {
  token: string | null;
  children: ReactNode;
}

export function WSProvider({ token, children }: WSProviderProps) {
  const handlersRef = useRef<Map<string, Set<MessageHandler>>>(new Map());
  const firstSentRef = useRef(false);

  const handleMessage = useCallback((msg: WsMessage) => {
    const handlers = handlersRef.current.get(msg.type);
    if (handlers) {
      for (const handler of handlers) {
        handler(msg);
      }
    }
  }, []);

  const { status, send, connect, disconnect } = useWebSocket({
    token: token ?? '',
    onMessage: handleMessage,
  });

  useEffect(() => {
    if (token) {
      connect();
    } else {
      disconnect();
    }
  }, [token, connect, disconnect]);

  const wrappedSend = useCallback(
    (msg: WsMessage) => {
      if (!firstSentRef.current) {
        firstSentRef.current = true;
        console.log('[WS ↑] first message sent', msg.type, msg);
      }
      send(msg);
    },
    [send],
  );

  const subscribe = useCallback((type: string, handler: MessageHandler): (() => void) => {
    const map = handlersRef.current;
    let set = map.get(type);
    if (!set) {
      set = new Set();
      map.set(type, set);
    }
    set.add(handler);
    return () => {
      map.get(type)?.delete(handler);
    };
  }, []);

  return (
    <WSContext.Provider value={{ status, send: wrappedSend, subscribe }}>
      {children}
    </WSContext.Provider>
  );
}

export function useWSContext(): WSContextValue {
  return useContext(WSContext);
}
